import cx from 'classnames';
import { useCallback } from 'react';
import { ConditionalView } from '../../utils/ConditionalView';
import Text from '../Text';
import Tooltip from '../Tooltip';
import styles from './Slider.module.scss';
import usePresenter from './Slider.presenter';
import { SliderProps } from './Slider.types';

const Slider = (props: SliderProps) => {
  const {
    variantProps,
    sliderRef,
    focusedThumb,
    getPercentage,
    enableRange,
    value,
    showMax,
    max,
    showMin,
    min,
    startDrag,
    showValueOnThumb,
    disabled,
    otherSliderProps,
  } = usePresenter(props);

  const toolTipView = useCallback(
    (show: boolean, value: number, index: number) => {
      const sliderThumb = <div
        key={index}
        className={styles.sliderThumb}
        style={{ left: `${getPercentage(value)}%` }}
        onTouchStart={startDrag(index)}
      />;

      if (showValueOnThumb && show) {
        return <Tooltip
          isVisible={showValueOnThumb && show}
          action='Follow'
          target={sliderThumb}
          style='Outlined'
        >
          {value}
        </Tooltip>;
      }
      return sliderThumb;
    },
    [showValueOnThumb],
  );

  return (
    <div
      className={cx(styles.sliderContainer,
        {
          [styles.disabled]: disabled,
        },
        props.className)}
      {...variantProps}
      {...otherSliderProps}
    >
      <ConditionalView condition={showMin}>
        <Text className={styles.sliderLabel}>{min}</Text>
      </ConditionalView>
      <div
        id={props.id}
        ref={sliderRef}
        className={styles.sliderTrack}
        onMouseDown={startDrag()}
        onTouchStart={startDrag()}
      >
        <div
          className={styles.sliderFill}
          style={
            enableRange
              ? {
                left: `${getPercentage((value as [number, number])[0])}%`,
                right: `${100 - getPercentage((value as [number, number])[1])}%`,
              }
              : {
                left: `0%`,
                right: `${100 - getPercentage(value as number)}%`,
              }
          }
        />
        {enableRange ? (
          (value as [number, number]).map((val, index) =>
            toolTipView(focusedThumb === index, val, index)
          )
        ) : (
          toolTipView(focusedThumb === 0, value as number, 0)
        )}
      </div>
      {showMax && <Text className={styles.sliderLabel}>{max}</Text>}
    </div>
  );
};

export default Slider;
